import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Lightbulb,
  Plus,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  BEHAVIOR_CHANGE_STEPS,
  MAX_BEHAVIORS,
  MIN_BEHAVIORS,
  type BehaviorChangeState,
  type BehaviorChangeStep,
  type BehaviorPattern,
  canAdvance,
  createBehavior,
  nextStep,
  prevStep,
  stepIndex,
} from '../lib/behaviorChange';

const STORAGE_KEY = 'lab_changing_unwanted_behaviors_v01';

const DEFAULT_STATE: BehaviorChangeState = {
  behaviors: [createBehavior()],
};

function loadState(): BehaviorChangeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<BehaviorChangeState>;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      behaviors:
        parsed.behaviors?.length ? parsed.behaviors : DEFAULT_STATE.behaviors,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveState(state: BehaviorChangeState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface ChangingUnwantedBehaviorsProps {
  embedded?: boolean;
  onComplete?: (state: BehaviorChangeState) => void;
}

export const ChangingUnwantedBehaviors: React.FC<ChangingUnwantedBehaviorsProps> = ({
  embedded = false,
  onComplete,
}) => {
  const [step, setStep] = useState<BehaviorChangeStep>('intro');
  const [state, setState] = useState<BehaviorChangeState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const progressPct = useMemo(
    () => ((stepIndex(step) + 1) / BEHAVIOR_CHANGE_STEPS.length) * 100,
    [step],
  );

  const updateBehavior = useCallback((id: string, updates: Partial<BehaviorPattern>) => {
    setState(prev => ({
      ...prev,
      behaviors: prev.behaviors.map(b => (b.id === id ? { ...b, ...updates } : b)),
    }));
  }, []);

  const addBehavior = useCallback(() => {
    if (state.behaviors.length >= MAX_BEHAVIORS) return;
    setState(prev => ({ ...prev, behaviors: [...prev.behaviors, createBehavior()] }));
  }, [state.behaviors.length]);

  const removeBehavior = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      behaviors:
        prev.behaviors.length <= MIN_BEHAVIORS
          ? prev.behaviors
          : prev.behaviors.filter(b => b.id !== id),
    }));
  }, []);

  const goNext = useCallback(() => {
    if (!canAdvance(step, state)) return;

    if (step === 'thoughts') {
      const completed = {
        ...state,
        completedAt: new Date().toISOString(),
      };
      setState(completed);
      onComplete?.(completed);
    }

    const n = nextStep(step);
    if (n) setStep(n);
  }, [step, state, onComplete]);

  const goBack = useCallback(() => {
    const p = prevStep(step);
    if (p) setStep(p);
  }, [step]);

  const reset = useCallback(() => {
    setState({ behaviors: [createBehavior()] });
    setStep('intro');
  }, []);

  const cardClass = embedded
    ? 'bg-surface-card border-surface-border'
    : 'bg-[var(--bg-raised)] border-charcoal-200 max-w-3xl mx-auto';

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-8'}>
      {!embedded && (
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <RefreshCw className="h-8 w-8 text-consulting-slate-blue-600" />
            <h1 className="text-3xl font-heading font-bold text-charcoal-900">
              Changing Unwanted Behaviors
            </h1>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
              v0.1
            </span>
          </div>
          <p className="text-charcoal-600 text-sm max-w-2xl mx-auto">
            Reprogram the pattern from the outside in: name the behavior, examine beliefs and
            fears, then change the thinking that keeps the old program running.
          </p>
        </div>
      )}

      {embedded && (
        <div className="mb-2">
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-widest mb-2">
            Behavior change · v0.1
          </div>
          <h2 className="font-serif text-2xl text-text-primary font-light">
            Changing Unwanted Behaviors
          </h2>
          <p className="font-sans text-text-secondary text-sm mt-2 max-w-2xl">
            Thoughts lead to beliefs that become behaviors. Change your thoughts, change your life.
          </p>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-3xl mx-auto w-full'}>
        <div className="flex items-center justify-between mb-2">
          <span
            className={`font-mono text-[10px] uppercase tracking-widest ${
              embedded ? 'text-text-dim' : 'text-charcoal-500'
            }`}
          >
            Step {stepIndex(step) + 1} of {BEHAVIOR_CHANGE_STEPS.length} —{' '}
            {BEHAVIOR_CHANGE_STEPS[stepIndex(step)]?.label}
          </span>
          <span className={`font-mono text-[10px] ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            {Math.round(progressPct)}%
          </span>
        </div>
        <div
          className={`h-1.5 rounded-full overflow-hidden ${
            embedded ? 'bg-surface-elevated' : 'bg-charcoal-100'
          }`}
        >
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              embedded ? 'bg-[var(--green)]' : 'bg-consulting-slate-blue-600'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div className={` border p-6 md:p-8 ${cardClass}`}>
        {step === 'intro' && <IntroStep embedded={embedded} />}
        {step === 'identify' && (
          <IdentifyStep
            embedded={embedded}
            behaviors={state.behaviors}
            onUpdate={updateBehavior}
            onAdd={addBehavior}
            onRemove={removeBehavior}
          />
        )}
        {step === 'behavior' && (
          <BehaviorStep embedded={embedded} behaviors={state.behaviors} onUpdate={updateBehavior} />
        )}
        {step === 'beliefs' && (
          <BeliefsStep embedded={embedded} behaviors={state.behaviors} onUpdate={updateBehavior} />
        )}
        {step === 'thoughts' && (
          <ThoughtsStep embedded={embedded} behaviors={state.behaviors} onUpdate={updateBehavior} />
        )}
        {step === 'results' && <ResultsStep embedded={embedded} state={state} />}
      </div>

      <div className={`flex items-center justify-between gap-4 ${embedded ? '' : 'max-w-3xl mx-auto'}`}>
        <button
          type="button"
          onClick={goBack}
          disabled={step === 'intro'}
          className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            embedded
              ? 'border-surface-border text-text-tertiary hover:bg-surface-elevated'
              : 'border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-2">
          {step === 'results' ? (
            <button
              type="button"
              onClick={reset}
              className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium ${
                embedded
                  ? 'border-surface-border text-text-tertiary hover:bg-surface-elevated'
                  : 'border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
              }`}
            >
              <RotateCcw className="h-4 w-4" />
              Start over
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance(step, state)}
              className={`inline-flex items-center gap-2 px-5 py-2  text-sm font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                embedded
                  ? 'bg-[var(--green)] hover:bg-[var(--green)]'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {step === 'thoughts' ? 'See my worksheet' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function IntroStep({ embedded }: { embedded: boolean }) {
  const card = embedded
    ? 'bg-surface-elevated border-surface-border text-text-secondary'
    : 'bg-consulting-slate-blue-50 border-consulting-slate-blue-200 text-charcoal-700';
  const heading = embedded ? 'text-text-primary' : 'text-charcoal-900';

  return (
    <div className="space-y-6">
      <div className={` border p-5 ${card}`}>
        <div className="flex items-start gap-3">
          <Brain className={`h-5 w-5 flex-shrink-0 mt-0.5 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
          <div>
            <h2 className={`font-semibold mb-2 ${heading}`}>The big idea</h2>
            <p className="text-sm leading-relaxed">
              Our thoughts lead us to adopt beliefs about the world that become behaviors. If you
              want to change your behaviors, you have to change your thinking. Think of this as
              reprogramming your mind — and know that the old program will keep running until the
              new programming becomes a habit.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 text-sm leading-relaxed">
        <p className={embedded ? 'text-text-secondary' : 'text-charcoal-700'}>
          <strong className={heading}>Start with what you can see:</strong> behavior is the easiest
          layer to recognize. Name your top unwanted patterns, then work backward through beliefs
          and thoughts to find the new thinking you need to adopt.
        </p>
        <p className={embedded ? 'text-text-secondary' : 'text-charcoal-700'}>
          <strong className={heading}>Set proper expectations:</strong> plan on at least 2–3 months
          to change a behavior. Where addiction is involved, expect 6 months to 2 years to fully
          replace the old programming. Change your thoughts, change your life.
        </p>
      </div>

      <div
        className={`grid sm:grid-cols-3 gap-3 text-sm ${
          embedded ? 'text-text-tertiary' : 'text-charcoal-600'
        }`}
      >
        {[
          { n: 'Behavior', t: 'Name the pattern', d: 'What you actually do when the old program runs' },
          { n: 'Beliefs', t: 'Surface what you obey', d: 'What the pattern taught you to believe — and fear' },
          { n: 'Thoughts', t: 'Rewrite the script', d: 'The thinking you must adopt to stop acting it out' },
        ].map(item => (
          <div
            key={item.n}
            className={` border p-4 ${
              embedded ? 'bg-surface-elevated border-surface-border' : 'bg-[var(--surface)] border-charcoal-100'
            }`}
          >
            <div
              className={`font-mono text-xs mb-2 ${
                embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'
              }`}
            >
              {item.n}
            </div>
            <div className={`font-medium mb-1 ${heading}`}>{item.t}</div>
            <div className="text-xs">{item.d}</div>
          </div>
        ))}
      </div>

      <div
        className={` border p-4 text-sm space-y-2 ${
          embedded ? 'border-surface-border bg-surface-elevated/50' : 'border-charcoal-100 bg-[var(--surface)]'
        }`}
      >
        <div className={`font-medium ${heading}`}>Quick framework</div>
        <ol className={`list-decimal pl-5 space-y-1 ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
          <li>Identify the unwanted behavior.</li>
          <li>Ask: &ldquo;What does this pattern make me willing and unwilling to do?&rdquo;</li>
          <li>Consider: &ldquo;What must change for me to stop acting on this pattern?&rdquo;</li>
          <li>Challenge: &ldquo;What fears drive this behavior?&rdquo;</li>
          <li>Decide: &ldquo;What must I do to overcome those fears?&rdquo;</li>
        </ol>
      </div>
    </div>
  );
}

function IdentifyStep({
  embedded,
  behaviors,
  onUpdate,
  onAdd,
  onRemove,
}: {
  embedded: boolean;
  behaviors: BehaviorPattern[];
  onUpdate: (id: string, updates: Partial<BehaviorPattern>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
        Identify your top {MIN_BEHAVIORS}–{MAX_BEHAVIORS} unwanted behavior patterns — the ones
        that show up often enough to cost you. Be specific: &ldquo;avoid hard conversations&rdquo;
        beats &ldquo;procrastination.&rdquo;
      </p>

      <div className="space-y-4">
        {behaviors.map((behavior, idx) => (
          <div
            key={behavior.id}
            className={` border p-4 space-y-3 ${
              embedded ? 'border-surface-border bg-surface-elevated/40' : 'border-charcoal-200'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={`font-mono text-[10px] uppercase tracking-widest ${
                  embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'
                }`}
              >
                Behavior pattern {idx + 1}
              </span>
              {behaviors.length > MIN_BEHAVIORS && (
                <button
                  type="button"
                  onClick={() => onRemove(behavior.id)}
                  className={embedded ? 'text-text-dim hover:text-[var(--coral)]' : 'text-charcoal-400 hover:text-[var(--coral)]'}
                  aria-label="Remove behavior"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <Field label="Describe the unwanted behavior" embedded={embedded}>
              <input
                value={behavior.label}
                onChange={e => onUpdate(behavior.id, { label: e.target.value })}
                placeholder="e.g. Say yes to every request before checking capacity"
                className={`w-full  border px-3 py-2 text-sm focus:outline-none ${input}`}
              />
            </Field>
          </div>
        ))}
      </div>

      {behaviors.length < MAX_BEHAVIORS && (
        <button
          type="button"
          onClick={onAdd}
          className={`inline-flex items-center gap-2 text-sm font-medium ${
            embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]' : 'text-primary-600 hover:text-primary-700'
          }`}
        >
          <Plus className="h-4 w-4" />
          Add another pattern ({behaviors.length}/{MAX_BEHAVIORS})
        </button>
      )}
    </div>
  );
}

function BehaviorStep({
  embedded,
  behaviors,
  onUpdate,
}: {
  embedded: boolean;
  behaviors: BehaviorPattern[];
  onUpdate: (id: string, updates: Partial<BehaviorPattern>) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <div
        className={` border p-4 flex items-start gap-3 ${
          embedded
            ? 'bg-[var(--green)]/5 border-[var(--green)]/20'
            : 'bg-consulting-slate-blue-50 border-consulting-slate-blue-200'
        }`}
      >
        <Lightbulb className={`h-5 w-5 flex-shrink-0 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
          Behavior is what you can observe. Map what the pattern makes you willing and unwilling
          to do today — then what you must be willing and unwilling to do in order to change it.
        </p>
      </div>

      <div className="space-y-5">
        {behaviors.map((behavior, idx) => (
          <BehaviorCard key={behavior.id} embedded={embedded} label={behavior.label || `Pattern ${idx + 1}`}>
            <Field
              label="1. When I act out this pattern, what am I willing and unwilling to do?"
              embedded={embedded}
            >
              <textarea
                value={behavior.willingUnwillingCurrent}
                onChange={e => onUpdate(behavior.id, { willingUnwillingCurrent: e.target.value })}
                placeholder="Willing to… Unwilling to…"
                rows={3}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
            <Field
              label="2. What must I be willing and unwilling to do to change this pattern?"
              embedded={embedded}
            >
              <textarea
                value={behavior.willingUnwillingToChange}
                onChange={e => onUpdate(behavior.id, { willingUnwillingToChange: e.target.value })}
                placeholder="What must change for me to stop acting on this pattern?"
                rows={3}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
          </BehaviorCard>
        ))}
      </div>
    </div>
  );
}

function BeliefsStep({
  embedded,
  behaviors,
  onUpdate,
}: {
  embedded: boolean;
  behaviors: BehaviorPattern[];
  onUpdate: (id: string, updates: Partial<BehaviorPattern>) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
        Beliefs are what the pattern taught you to obey — often driven by fear. Name them, then
        decide what you must be willing to believe instead, and what you will do to overcome the
        fears that keep the old program running.
      </p>

      <div className="space-y-5">
        {behaviors.map((behavior, idx) => (
          <BehaviorCard key={behavior.id} embedded={embedded} label={behavior.label || `Pattern ${idx + 1}`}>
            <Field label="3. What has this pattern made me believe?" embedded={embedded}>
              <textarea
                value={behavior.beliefsHeld}
                onChange={e => onUpdate(behavior.id, { beliefsHeld: e.target.value })}
                placeholder="The story I tell myself when this pattern runs…"
                rows={2}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
            <Field
              label="4. What must I be willing to believe in order to not obey these beliefs?"
              embedded={embedded}
            >
              <textarea
                value={behavior.beliefsNeeded}
                onChange={e => onUpdate(behavior.id, { beliefsNeeded: e.target.value })}
                placeholder="A belief I can practice until it sticks…"
                rows={2}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
            <Field label="What fears drive this behavior?" embedded={embedded}>
              <textarea
                value={behavior.fearsDriving}
                onChange={e => onUpdate(behavior.id, { fearsDriving: e.target.value })}
                placeholder="If I stop this pattern, I fear that…"
                rows={2}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
            <Field label="What must I do to overcome those fears?" embedded={embedded}>
              <textarea
                value={behavior.overcomeFears}
                onChange={e => onUpdate(behavior.id, { overcomeFears: e.target.value })}
                placeholder="Concrete actions, supports, or experiments…"
                rows={2}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
          </BehaviorCard>
        ))}
      </div>
    </div>
  );
}

function ThoughtsStep({
  embedded,
  behaviors,
  onUpdate,
}: {
  embedded: boolean;
  behaviors: BehaviorPattern[];
  onUpdate: (id: string, updates: Partial<BehaviorPattern>) => void;
}) {
  const input = embedded
    ? 'bg-surface-card border-surface-border text-text-primary placeholder-text-dim focus:border-[var(--green)]/40'
    : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-900 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100';

  return (
    <div className="space-y-6">
      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
        Thoughts are the source code. Capture what you have been thinking — then write the specific
        shift in thinking that makes you no longer willing to act out the pattern.
      </p>

      <div className="space-y-5">
        {behaviors.map((behavior, idx) => (
          <BehaviorCard key={behavior.id} embedded={embedded} label={behavior.label || `Pattern ${idx + 1}`}>
            <Field label="5. What have I been thinking as it relates to this pattern?" embedded={embedded}>
              <textarea
                value={behavior.currentThinking}
                onChange={e => onUpdate(behavior.id, { currentThinking: e.target.value })}
                placeholder="The inner monologue that precedes the behavior…"
                rows={3}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
            <Field
              label="6. What part of my thinking must change so I am no longer willing to act out this pattern?"
              embedded={embedded}
            >
              <textarea
                value={behavior.thinkingToChange}
                onChange={e => onUpdate(behavior.id, { thinkingToChange: e.target.value })}
                placeholder="The new thought I will rehearse until it becomes habit…"
                rows={3}
                className={`w-full  border px-3 py-2 text-sm focus:outline-none resize-none ${input}`}
              />
            </Field>
          </BehaviorCard>
        ))}
      </div>
    </div>
  );
}

function ResultsStep({
  embedded,
  state,
}: {
  embedded: boolean;
  state: BehaviorChangeState;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Sparkles className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Your behavior change worksheet
        </h2>
      </div>

      <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        Keep rehearsing the new thinking. The old program will still run — build the new programming
        until it becomes the default. Expect months, not days.
      </p>

      <div className="space-y-6">
        {state.behaviors.map((behavior, idx) => (
          <div
            key={behavior.id}
            className={` border overflow-hidden ${
              embedded ? 'border-surface-border' : 'border-charcoal-200'
            }`}
          >
            <div
              className={`px-5 py-3 border-b ${
                embedded
                  ? 'bg-surface-elevated border-surface-border'
                  : 'bg-consulting-slate-blue-50 border-consulting-slate-blue-100'
              }`}
            >
              <div className="flex items-baseline gap-3">
                <span
                  className={`font-mono text-xs ${embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'}`}
                >
                  {idx + 1}
                </span>
                <h3 className={`text-lg font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
                  {behavior.label}
                </h3>
              </div>
            </div>

            <div className="p-5 space-y-5">
              <ResultSection embedded={embedded} title="Behavior" items={[
                { q: 'Willing / unwilling (current)', a: behavior.willingUnwillingCurrent },
                { q: 'Willing / unwilling (to change)', a: behavior.willingUnwillingToChange },
              ]} />
              <ResultSection embedded={embedded} title="Beliefs" items={[
                { q: 'What I believe', a: behavior.beliefsHeld },
                { q: 'What I must believe instead', a: behavior.beliefsNeeded },
                { q: 'Fears driving the behavior', a: behavior.fearsDriving },
                { q: 'Overcoming those fears', a: behavior.overcomeFears },
              ]} />
              <ResultSection embedded={embedded} title="Thoughts" items={[
                { q: 'Current thinking', a: behavior.currentThinking },
                { q: 'Thinking to change', a: behavior.thinkingToChange },
              ]} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BehaviorCard({
  embedded,
  label,
  children,
}: {
  embedded: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={` border p-4 space-y-4 ${
        embedded ? 'border-surface-border bg-surface-elevated/40' : 'border-charcoal-200 bg-[var(--surface)]/50'
      }`}
    >
      <div
        className={`text-sm font-medium ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultSection({
  embedded,
  title,
  items,
}: {
  embedded: boolean;
  title: string;
  items: { q: string; a: string }[];
}) {
  return (
    <div>
      <div
        className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${
          embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'
        }`}
      >
        {title}
      </div>
      <div className="space-y-3">
        {items.map(item => (
          <div key={item.q}>
            <div className={`text-xs font-medium mb-1 ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
              {item.q}
            </div>
            <div className={`text-sm whitespace-pre-wrap ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
              {item.a || '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  embedded,
  children,
}: {
  label: string;
  embedded: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span
        className={`text-xs font-medium leading-snug ${
          embedded ? 'text-text-dim' : 'text-charcoal-500'
        }`}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
